import React from 'react';
import { useField } from 'react-final-form';

// Import config and utils
import { useIntl } from '../../util/reactIntl';
import {
  SCHEMA_TYPE_ENUM,
  SCHEMA_TYPE_MULTI_ENUM,
  SCHEMA_TYPE_TEXT,
  SCHEMA_TYPE_SHORT_TEXT,
  SCHEMA_TYPE_LONG,
  SCHEMA_TYPE_BOOLEAN,
  SCHEMA_TYPE_YOUTUBE,
} from '../../util/types';
import {
  required,
  nonEmptyArray,
  validateInteger,
  validateYoutubeURL,
} from '../../util/validators';
// Import shared components
import { FieldCheckboxGroup, FieldSelect, FieldTextInput, FieldBoolean } from '../../components';
// Import modules from this directory
import css from './CustomExtendedDataField.module.css';
import GradingInfo from '../GradingInfo/GradingInfo';

const createFilterOptions = (options, fieldKey, intl) =>
  options.map(o => ({
    key: `${o.option}`,
    label: intl.formatMessage({
      id: `ListingPage.field.${fieldKey}.option.${o.option}`,
      defaultMessage: o.label || o.option,
    }),
  }));

const getLabel = fieldConfig => fieldConfig?.saveConfig?.label || fieldConfig?.label;

const CustomFieldEnum = props => {
  const { name, fieldConfig, defaultRequiredMessage, formId, intl } = props;
  const { enumOptions = [], saveConfig, key: fieldKey } = fieldConfig || {};
  const { placeholderMessage, isRequired, requiredMessage } = saveConfig || {};
  const validateMaybe = isRequired
    ? { validate: required(requiredMessage || defaultRequiredMessage) }
    : {};

  const genericPlaceholder = intl.formatMessage({ id: 'CustomExtendedDataField.placeholderSingleSelect' });
  const placeholder =
    placeholderMessage ||
    intl.formatMessage({
      id: `CustomExtendedDataField.placeholderSingleSelect.${fieldConfig.key}`,
      defaultMessage: genericPlaceholder,
    });
  const filterOptions = createFilterOptions(enumOptions, fieldKey, intl);

  const label = intl.formatMessage({
    id: `ListingPage.field.${fieldKey}.label`,
    defaultMessage: getLabel(fieldConfig) || fieldKey,
  });

  const GradingInfoMaybe = ['sleeve_condition', 'vinyl_condition', 'dimensions']?.includes(fieldConfig.key) ? (
    <GradingInfo type={fieldConfig.key === 'dimensions' ? 'dimensions' : 'grading'} />
  ) : null;

  const fieldId = formId ? `${formId}.${name}` : name;

  const { input } = useField(name, { subscription: { value: true } });
  const isDimensions = fieldKey === 'dimensions';
  const showCustomDimensions = isDimensions && input.value === 'custom-size';

  return filterOptions ? (
    <div className={css.customField}>
      <div className={css.labelRow}>
        <label className={css.label} htmlFor={fieldId}>
          {label}
        </label>
        {GradingInfoMaybe}
      </div>

      <FieldSelect name={name} id={fieldId} label={null} {...validateMaybe}>
        <option disabled value="">
          {placeholder}
        </option>
        {filterOptions.map(optionConfig => {
          const key = optionConfig.key;
          return (
            <option key={key} value={key}>
              {optionConfig.label}
            </option>
          );
        })}
      </FieldSelect>

      {showCustomDimensions && (
        <div>
          <FieldTextInput
            className={css.customDimensionsRow}
            id={formId ? `${formId}.pub_custom_dimensions` : 'pub_custom_dimensions'}
            name="pub_custom_dimensions"
            type="text"
            label={intl.formatMessage({ id: 'CustomExtendedDataField.customDimensionsLabel', defaultMessage: 'Enter custom dimensions' })}
            placeholder={intl.formatMessage({ id: 'CustomExtendedDataField.customDimensionsPlaceholder', defaultMessage: 'e.g. 100 x 200 cm' })}
            validate={required(requiredMessage || defaultRequiredMessage)}
          />
          <FieldTextInput
            className={css.customDimensionsRow}
            id={formId ? `${formId}.pub_custom_dimensions_description` : 'pub_custom_dimensions_description'}
            name="pub_custom_dimensions_description"
            type="text"
            label={intl.formatMessage({ id: 'CustomExtendedDataField.customDimensionsDescriptionLabel', defaultMessage: 'Custom Size Description' })}
            placeholder={intl.formatMessage({ id: 'CustomExtendedDataField.customDimensionsDescriptionPlaceholder', defaultMessage: 'e.g. Framed, slightly trimmed' })}
          />
        </div>
      )}
    </div>
  ) : null;
};

const CustomFieldMultiEnum = props => {
  const { name, fieldConfig, defaultRequiredMessage, formId, intl } = props;
  const { enumOptions = [], saveConfig, key: fieldKey } = fieldConfig || {};
  const { isRequired, requiredMessage } = saveConfig || {};
  const label = intl.formatMessage({
    id: `ListingPage.field.${fieldKey}.label`,
    defaultMessage: getLabel(fieldConfig) || fieldKey,
  });
  const validateMaybe = isRequired
    ? { validate: nonEmptyArray(requiredMessage || defaultRequiredMessage) }
    : {};

  return enumOptions ? (
    <FieldCheckboxGroup
      className={css.customField}
      id={formId ? `${formId}.${name}` : name}
      name={name}
      label={label}
      options={createFilterOptions(enumOptions, fieldKey, intl)}
      {...validateMaybe}
    />
  ) : null;
};

const CustomFieldText = props => {
  const { name, fieldConfig, defaultRequiredMessage, formId, intl } = props;
  const { placeholderMessage, isRequired, requiredMessage } = fieldConfig?.saveConfig || {};
  const label = intl.formatMessage({
    id: `ListingPage.field.${fieldConfig.key}.label`,
    defaultMessage: getLabel(fieldConfig) || fieldConfig.key,
  });
  const validateMaybe = isRequired
    ? { validate: required(requiredMessage || defaultRequiredMessage) }
    : {};
  const genericPlaceholder = intl.formatMessage({ id: 'CustomExtendedDataField.placeholderText' });
  const placeholder =
    placeholderMessage ||
    intl.formatMessage({
      id: `CustomExtendedDataField.placeholderText.${fieldConfig.key}`,
      defaultMessage: genericPlaceholder,
    });

  return (
    <FieldTextInput
      className={css.customField}
      id={formId ? `${formId}.${name}` : name}
      name={name}
      type="textarea"
      label={label}
      placeholder={placeholder}
      {...validateMaybe}
    />
  );
};

const CustomFieldShortText = props => {
  const { name, fieldConfig, defaultRequiredMessage, formId, intl } = props;
  const { placeholderMessage, isRequired, requiredMessage } = fieldConfig?.saveConfig || {};
  const label = intl.formatMessage({
    id: `ListingPage.field.${fieldConfig.key}.label`,
    defaultMessage: getLabel(fieldConfig) || fieldConfig.key,
  });
  const validateMaybe = isRequired
    ? { validate: required(requiredMessage || defaultRequiredMessage) }
    : {};
  const genericPlaceholder = intl.formatMessage({ id: 'CustomExtendedDataField.placeholderText' });
  const placeholder =
    placeholderMessage ||
    intl.formatMessage({
      id: `CustomExtendedDataField.placeholderText.${fieldConfig.key}`,
      defaultMessage: genericPlaceholder,
    });

  return (
    <FieldTextInput
      className={css.customField}
      id={formId ? `${formId}.${name}` : name}
      name={name}
      type="text"
      label={label}
      placeholder={placeholder}
      {...validateMaybe}
    />
  );
};

const CustomFieldLong = props => {
  const { name, fieldConfig, defaultRequiredMessage, formId, intl } = props;
  const { minimum, maximum, saveConfig } = fieldConfig;
  const { placeholderMessage, isRequired, requiredMessage } = saveConfig || {};
  const label = intl.formatMessage({
    id: `ListingPage.field.${fieldConfig.key}.label`,
    defaultMessage: getLabel(fieldConfig) || fieldConfig.key,
  });
  const placeholder =
    placeholderMessage || intl.formatMessage({ id: 'CustomExtendedDataField.placeholderLong' });
  const numberTooSmallMessage = intl.formatMessage(
    { id: 'CustomExtendedDataField.numberTooSmall' },
    { min: minimum }
  );
  const numberTooBigMessage = intl.formatMessage(
    { id: 'CustomExtendedDataField.numberTooBig' },
    { max: maximum }
  );

  // Field with schema type 'long' will always be validated against min & max
  const validate = (value, min, max) => {
    const requiredMsg = requiredMessage || defaultRequiredMessage;
    return isRequired && value == null
      ? requiredMsg
      : validateInteger(value, max, min, numberTooSmallMessage, numberTooBigMessage);
  };

  return (
    <FieldTextInput
      className={css.customField}
      id={formId ? `${formId}.${name}` : name}
      name={name}
      type="number"
      step="1"
      parse={value => {
        const parsed = Number.parseInt(value, 10);
        return Number.isNaN(parsed) ? null : parsed;
      }}
      label={label}
      placeholder={placeholder}
      validate={value => validate(value, minimum, maximum)}
      onWheel={e => {
        // fix: number input should not change value on scroll
        if (e.target === document.activeElement) {
          // Prevent the input value change, because we prefer page scrolling
          e.target.blur();

          // Refocus immediately, on the next tick (after the current function is done)
          setTimeout(() => {
            e.target.focus();
          }, 0);
        }
      }}
    />
  );
};

const CustomFieldBoolean = props => {
  const { name, fieldConfig, defaultRequiredMessage, formId, intl } = props;
  const { placeholderMessage, isRequired, requiredMessage } = fieldConfig?.saveConfig || {};
  const label = intl.formatMessage({
    id: `ListingPage.field.${fieldConfig.key}.label`,
    defaultMessage: getLabel(fieldConfig) || fieldConfig.key,
  });
  const validateMaybe = isRequired
    ? { validate: required(requiredMessage || defaultRequiredMessage) }
    : {};
  const placeholder =
    placeholderMessage || intl.formatMessage({ id: 'CustomExtendedDataField.placeholderBoolean' });

  return (
    <FieldBoolean
      className={css.customField}
      id={formId ? `${formId}.${name}` : name}
      name={name}
      label={label}
      placeholder={placeholder}
      {...validateMaybe}
    />
  );
};

const CustomFieldYoutube = props => {
  const { name, fieldConfig, defaultRequiredMessage, formId, intl } = props;
  const { placeholderMessage, isRequired, requiredMessage } = fieldConfig?.saveConfig || {};
  const label = intl.formatMessage({
    id: `ListingPage.field.${fieldConfig.key}.label`,
    defaultMessage: getLabel(fieldConfig) || fieldConfig.key,
  });
  const placeholder =
    placeholderMessage ||
    intl.formatMessage({ id: 'CustomExtendedDataField.placeholderYoutubeVideoURL' });

  const notValidUrlMessage = intl.formatMessage({
    id: 'CustomExtendedDataField.notValidYoutubeVideoURL',
  });

  const validate = value => {
    const requiredMsg = requiredMessage || defaultRequiredMessage;
    return isRequired && value == null
      ? requiredMsg
      : validateYoutubeURL(value, notValidUrlMessage);
  };

  return (
    <FieldTextInput
      className={css.customField}
      id={formId ? `${formId}.${name}` : name}
      name={name}
      type="text"
      label={label}
      placeholder={placeholder}
      validate={value => validate(value)}
    />
  );
};

/**
 * Return Final Form field for each configuration according to schema type.
 *
 * These custom extended data fields are for generating input fields from configuration defined
 * in marketplace-custom-config.js. Other panels in EditListingWizard might add more extended data
 * fields (e.g. shipping fee), but these are independently customizable.
 *
 * @param {Object} props should contain fieldConfig that defines schemaType, enumOptions?, and
 * saveConfig for the field.
 */
const CustomExtendedDataField = props => {
  const intl = useIntl();
  const { enumOptions = [], schemaType } = props?.fieldConfig || {};
  const renderFieldComponent = (FieldComponent, props) => <FieldComponent {...props} intl={intl} />;

  return schemaType === SCHEMA_TYPE_ENUM && enumOptions
    ? renderFieldComponent(CustomFieldEnum, props)
    : schemaType === SCHEMA_TYPE_MULTI_ENUM && enumOptions
      ? renderFieldComponent(CustomFieldMultiEnum, props)
      : schemaType === SCHEMA_TYPE_TEXT
        ? renderFieldComponent(CustomFieldText, props)
        : schemaType === SCHEMA_TYPE_SHORT_TEXT
          ? renderFieldComponent(CustomFieldShortText, props)
          : schemaType === SCHEMA_TYPE_LONG
            ? renderFieldComponent(CustomFieldLong, props)
            : schemaType === SCHEMA_TYPE_BOOLEAN
              ? renderFieldComponent(CustomFieldBoolean, props)
              : schemaType === SCHEMA_TYPE_YOUTUBE
                ? renderFieldComponent(CustomFieldYoutube, props)
                : null;
};

export default CustomExtendedDataField;
